import React, { RefObject, useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import classnames from "classnames";
import { useRouter } from "next/router";

import { getPlatformStatI18nLabels } from "@vahak/core/dist/constants/platformStats";
import { WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import VahakLogoBlue from "@vahak/core/dist/icons/VahakLogoBlue_n.svg";
import VahakLogoWhite from "@vahak/core/dist/icons/VahakLogoWhite_n.svg";

import PageMetaTags from "../common/page-meta-tags/PageMetaTags";
import { useCompactLoadPostingOptional } from "../homepage-v2/compact-load-posting/CompactLoadPostingProvider";
import { getHomeUrl } from "../../methods/pageSeo";

import LcvRouteIcon from "./LcvRouteIcon";

import {
    LcvRouteConfig,
    LcvRouteLocalizedContent,
    LcvRoutePageChip,
    LcvRoutePageFaq,
    LcvRoutePageLink
} from "./data/types";
import {
    LCV_ROUTE_PLAY_STORE_URL,
    LCV_ROUTE_SOCIAL_LINKS,
    LCV_BOOKING_ROUTE_SLUGS,
    getLcvBookingRoutePath
} from "./lcvRouteLanding.constants";
import Styles from "./LcvRouteLandingPage.module.scss";
import useLcvRouteLandingTranslation from "./useLcvRouteLandingTranslation";

interface LcvRouteFooterGroup {
    label: string;
    links?: LcvRoutePageLink[];
    textItems?: string[];
    moreLink?: LcvRoutePageLink;
}

interface LcvRouteLandingPageProps {
    routeConfig: LcvRouteConfig;
}

const RouteLink = ({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) => {
    return (
        <Link href={href}>
            <a className={className}>{children}</a>
        </Link>
    );
};

const Header = () => {
    return (
        <header className={Styles["site-header"]}>
            <RouteLink href="/" className={Styles["logo-link"]}>
                <VahakLogoBlue className={Styles["logo-svg"]} aria-label="Vahak" />
            </RouteLink>
        </header>
    );
};

const Hero = ({
    routeContent,
    formRef,
    heroCtaRef,
    onBookLorryNow,
    isCompactPostingOpen,
    eyebrow,
    headline1,
    ctaLabel,
    priceEstimatePrompt,
    priceEstimateLink,
    pickupAriaLabel,
    dropAriaLabel
}: {
    routeContent: LcvRouteLocalizedContent;
    formRef: RefObject<HTMLFormElement>;
    heroCtaRef: RefObject<HTMLButtonElement>;
    onBookLorryNow: () => void;
    isCompactPostingOpen: boolean;
    eyebrow: string;
    headline1: string;
    ctaLabel: string;
    priceEstimatePrompt: string;
    priceEstimateLink: string;
    pickupAriaLabel: string;
    dropAriaLabel: string;
}) => {
    return (
        <section className={Styles.hero}>
            <div className={Styles["hero-copy"]}>
                <p className={Styles["eyebrow-kw"]}>{eyebrow}</p>
                <h1 className={Styles["hero-h1"]}>
                    <span className={Styles["headline-1"]}>{headline1}</span>
                    {routeContent.heroHeadline2Lines.map((line) => (
                        <span className={Styles["headline-2"]} key={line}>
                            {line}
                        </span>
                    ))}
                </h1>
                <p className={Styles["hero-sub"]}>{routeContent.heroSub}</p>
            </div>

            <div className={Styles["hero-form-card"]}>
                <form
                    className={Styles["load-form"]}
                    id="load-form"
                    ref={formRef}
                    onSubmit={(event) => {
                        event.preventDefault();
                        onBookLorryNow();
                    }}
                >
                    <div className={Styles.field}>
                        <LcvRouteIcon iconClassName="ti ti-map-pin" />
                        <input
                            type="text"
                            name="pickup"
                            id="pickup"
                            defaultValue={routeContent.pickupCity}
                            aria-label={pickupAriaLabel}
                            readOnly
                        />
                    </div>
                    <div className={Styles.field}>
                        <LcvRouteIcon iconClassName="ti ti-navigation" />
                        <input
                            type="text"
                            name="drop"
                            id="drop"
                            defaultValue={routeContent.dropCity}
                            aria-label={dropAriaLabel}
                            readOnly
                        />
                    </div>
                    <div className={Styles["hero-actions"]}>
                        <button
                            type="submit"
                            className={Styles["btn-primary"]}
                            id="hero-cta-btn"
                            ref={heroCtaRef}
                            disabled={isCompactPostingOpen}
                        >
                            {ctaLabel}
                        </button>
                        <p className={Styles["price-link"]}>
                            {priceEstimatePrompt}{" "}
                            <a
                                href="#"
                                onClick={(event) => {
                                    event.preventDefault();
                                    if (isCompactPostingOpen) {
                                        return;
                                    }
                                    onBookLorryNow();
                                }}
                                aria-disabled={isCompactPostingOpen}
                            >
                                {priceEstimateLink}
                            </a>
                        </p>
                    </div>
                </form>
            </div>
        </section>
    );
};

const TrustStrip = ({ text }: { text: string }) => {
    return (
        <div className={Styles["trust-strip"]}>
            <LcvRouteIcon iconClassName="ti ti-shield-check" />
            <span>{text}</span>
        </div>
    );
};

const HowItWorks = ({ title, steps }: { title: string; steps: string[] }) => {
    return (
        <div className={Styles["steps-block"]}>
            <h2 className={Styles["steps-title"]}>{title}</h2>
            {steps.map((text, index) => (
                <div className={Styles["step-row"]} key={text}>
                    <span className={Styles["step-num"]}>{index + 1}</span>
                    <span className={Styles["step-text"]}>{text}</span>
                </div>
            ))}
        </div>
    );
};

const AboutBlock = ({ title, aboutText }: { title: string; aboutText: string }) => {
    return (
        <div className={Styles["about-block"]}>
            <h2 className={Styles["about-title"]}>{title}</h2>
            <p className={Styles["about-text"]}>{aboutText}</p>
        </div>
    );
};

const ProofModule = ({
    proofLabel,
    stats,
    features
}: {
    proofLabel: string;
    stats: Array<{ value: string; label: string }>;
    features: Array<{ iconClassName: string; label: string }>;
}) => {
    return (
        <>
            <div className={Styles["proof-card"]}>
                <p className={Styles["proof-label"]}>{proofLabel}</p>
                <div className={Styles["stat-grid"]}>
                    {stats.map((item) => (
                        <div className={Styles["stat-cell"]} key={item.label}>
                            <p className={Styles["stat-num"]}>{item.value}</p>
                            <p className={Styles["stat-label"]}>{item.label}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className={Styles["feature-row"]}>
                {features.map((item) => (
                    <div className={Styles["feature-item"]} key={item.label}>
                        <LcvRouteIcon iconClassName={item.iconClassName} />
                        <span>{item.label}</span>
                    </div>
                ))}
            </div>
        </>
    );
};

const ChipBlock = ({ label, chips }: { label: string; chips: LcvRoutePageChip[] }) => {
    return (
        <div className={Styles["chip-block"]}>
            <p className={Styles["chip-label"]}>{label}</p>
            <div className={Styles["chip-row"]}>
                {chips.map((chip) =>
                    chip.href ? (
                        <RouteLink className={Styles.chip} href={chip.href} key={chip.label}>
                            {chip.label}
                        </RouteLink>
                    ) : (
                        <span className={Styles.chip} key={chip.label}>
                            {chip.label}
                        </span>
                    )
                )}
            </div>
        </div>
    );
};

const SeoContent = ({
    routeContent,
    faqTitle,
    popularRoutesLabel,
    vehicleTypesLabel,
    allLcvRoutesLabel,
    allLcvRouteChips
}: {
    routeContent: LcvRouteLocalizedContent;
    faqTitle: string;
    popularRoutesLabel: string;
    vehicleTypesLabel: string;
    allLcvRoutesLabel: string;
    allLcvRouteChips: LcvRoutePageChip[];
}) => {
    return (
        <>
            <div className={Styles["faq-block"]}>
                <h2 className={Styles["block-title"]}>{faqTitle}</h2>

                {routeContent.faqs.map((faq: LcvRoutePageFaq) => (
                    <details className={Styles["faq-item"]} open={faq.defaultOpen} key={faq.question}>
                        <summary>{faq.question}</summary>
                        <p className={Styles["faq-answer"]}>{faq.answer}</p>
                    </details>
                ))}
            </div>

            <ChipBlock label={allLcvRoutesLabel} chips={allLcvRouteChips} />
            <ChipBlock label={popularRoutesLabel} chips={routeContent.popularRoutes} />
            <ChipBlock label={vehicleTypesLabel} chips={routeContent.vehicleTypes} />
        </>
    );
};

const FooterGroup = ({ group }: { group: LcvRouteFooterGroup }) => {
    return (
        <div className={Styles["footer-group"]}>
            <p className={Styles["fg-label"]}>{group.label}</p>
            <div className={Styles["fg-links"]}>
                {group.links?.map((link: LcvRoutePageLink) => (
                    <RouteLink href={link.href} key={link.href}>
                        {link.label}
                    </RouteLink>
                ))}
                {group.textItems?.map((item) => (
                    <span className={Styles["fg-text"]} key={item}>
                        {item}
                    </span>
                ))}
                {group.moreLink ? (
                    <RouteLink className={Styles["fg-more"]} href={group.moreLink.href}>
                        {group.moreLink.label}
                    </RouteLink>
                ) : null}
            </div>
        </div>
    );
};

const Footer = ({
    footerSeoText,
    brandText,
    groups,
    playStoreLabel,
    copyright,
    madeInIndia
}: {
    footerSeoText: string;
    brandText: string;
    groups: LcvRouteFooterGroup[];
    playStoreLabel: string;
    copyright: string;
    madeInIndia: string;
}) => {
    return (
        <footer className={Styles["site-footer"]}>
            <div className={Styles["footer-seo-text"]}>
                <p>{footerSeoText}</p>
            </div>

            <div className={Styles["wrap-footer"]}>
                <div className={Styles["footer-brand"]}>
                    <div className={Styles["logo-row"]}>
                        <RouteLink href="/" className={Styles["logo-link"]}>
                            <VahakLogoWhite className={Styles["footer-logo-svg"]} aria-label="Vahak" />
                        </RouteLink>
                    </div>
                    <p>{brandText}</p>
                </div>

                {groups.map((group) => (
                    <FooterGroup group={group} key={group.label} />
                ))}
            </div>

            <div className={Styles["footer-social"]}>
                {LCV_ROUTE_SOCIAL_LINKS.map((socialLink) => (
                    <a
                        key={socialLink.href}
                        href={socialLink.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={socialLink.label}
                    >
                        <LcvRouteIcon iconClassName={socialLink.iconClassName} />
                    </a>
                ))}
            </div>

            <div className={Styles["footer-app"]}>
                <a href={LCV_ROUTE_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                    <LcvRouteIcon iconClassName="ti ti-brand-google-play" />
                    <span>{playStoreLabel}</span>
                </a>
            </div>

            <div className={Styles["footer-bottom"]}>
                <p>{copyright}</p>
                <span className={Styles["footer-made"]}>{madeInIndia}</span>
            </div>
        </footer>
    );
};

const StickyCta = ({
    isVisible,
    onClick,
    isCompactPostingOpen,
    ctaLabel
}: {
    isVisible: boolean;
    onClick: () => void;
    isCompactPostingOpen: boolean;
    ctaLabel: string;
}) => {
    return (
        <div className={classnames(Styles["sticky-cta"], isVisible && Styles["is-visible"])}>
            <button type="button" className={Styles["btn-primary"]} onClick={onClick} disabled={isCompactPostingOpen}>
                {ctaLabel}
            </button>
        </div>
    );
};

const LcvRouteLandingPage = ({ routeConfig }: LcvRouteLandingPageProps) => {
    const { t } = useLcvRouteLandingTranslation();
    const { locale = "en" } = useRouter();
    const compactPosting = useCompactLoadPostingOptional();
    const formRef = useRef<HTMLFormElement>(null);
    const heroCtaRef = useRef<HTMLButtonElement>(null);
    const [isStickyCtaVisible, setIsStickyCtaVisible] = useState(false);
    const isCompactPostingOpen = Boolean(compactPosting?.isCompactPostingOpen);

    const routeContent = t(`routes.${routeConfig.slug}`, { returnObjects: true }) as LcvRouteLocalizedContent;
    const howItWorksSteps = t("howItWorks.steps", { returnObjects: true }) as string[];
    const proofStats = t("proof.stats", { returnObjects: true }) as Array<{ value: string; label: string }>;
    const features = t("features", { returnObjects: true }) as Array<{ iconClassName: string; label: string }>;
    const footerGroups = t("footer.groups", { returnObjects: true }) as LcvRouteFooterGroup[];
    const lcvRouteLabels = t("footer.lcvRouteLabels", { returnObjects: true }) as Record<string, string>;
    const allLcvRouteChips: LcvRoutePageChip[] = LCV_BOOKING_ROUTE_SLUGS.map((slug) => ({
        label: lcvRouteLabels[slug],
        href: getLcvBookingRoutePath(slug)
    }));
    const faqList = routeContent.faqs.map((faq) => ({
        question: faq.question,
        answer: faq.answer
    }));

    const isKannada = locale === "kn";
    const isHindi = locale === "hi" || locale === "mr";
    const isTelugu = locale === "te";

    const handleBookLorryNow = useCallback(() => {
        compactPosting?.openCompactPosting(
            {
                source: routeConfig.source,
                destination: routeConfig.destination
            },
            {
                eventData: { source_screen: WEB_V2_SOURCE_SCREENS.LCV_ROUTE_LANDING }
            }
        );
    }, [compactPosting, routeConfig.destination, routeConfig.source]);

    useEffect(() => {
        const heroBtn = heroCtaRef.current;

        if (!heroBtn || typeof IntersectionObserver === "undefined") {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsStickyCtaVisible(!entry.isIntersecting);
                });
            },
            { threshold: 0 }
        );

        observer.observe(heroBtn);

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <>
            <PageMetaTags
                title={routeContent.seo.title}
                description={routeContent.seo.description}
                locale={locale}
                localeNeutralPath={getLcvBookingRoutePath(routeConfig.slug)}
                ogType="website"
                ogSiteName="Vahak"
                ogImgAltText={routeContent.seo.title}
                includeDefaultSchema
                schema={{
                    breadcrumbItems: [
                        { name: t("meta.breadcrumbHome"), url: getHomeUrl() },
                        { name: t("meta.breadcrumbLcvBooking"), path: "/lcv-booking" },
                        { name: routeContent.breadcrumbRouteName }
                    ],
                    faqList
                }}
            />
            <main
                className={classnames(
                    Styles.page,
                    isKannada && Styles.pageKn,
                    isHindi && Styles.pageHi,
                    isTelugu && Styles.pageTe
                )}
                lang={locale}
            >
                <Header />
                <Hero
                    routeContent={routeContent}
                    formRef={formRef}
                    heroCtaRef={heroCtaRef}
                    onBookLorryNow={handleBookLorryNow}
                    isCompactPostingOpen={isCompactPostingOpen}
                    eyebrow={t("hero.eyebrow")}
                    headline1={t("hero.headline1")}
                    ctaLabel={t("hero.cta")}
                    priceEstimatePrompt={t("hero.priceEstimatePrompt")}
                    priceEstimateLink={t("hero.priceEstimateLink")}
                    pickupAriaLabel={t("hero.pickupAriaLabel")}
                    dropAriaLabel={t("hero.dropAriaLabel")}
                />
                <TrustStrip text={t("trustStrip", getPlatformStatI18nLabels())} />
                <HowItWorks title={t("howItWorks.title")} steps={howItWorksSteps} />
                <AboutBlock title={t("about.title")} aboutText={routeContent.aboutText} />
                <ProofModule proofLabel={t("proof.label")} stats={proofStats} features={features} />
                <SeoContent
                    routeContent={routeContent}
                    faqTitle={t("faq.title")}
                    popularRoutesLabel={t("chips.popularRoutes")}
                    vehicleTypesLabel={t("chips.vehicleTypes")}
                    allLcvRoutesLabel={t("chips.allLcvRoutes")}
                    allLcvRouteChips={allLcvRouteChips}
                />
                <Footer
                    footerSeoText={routeContent.footerSeoText}
                    brandText={t("footer.brandText")}
                    groups={footerGroups}
                    playStoreLabel={t("footer.playStore")}
                    copyright={t("footer.copyright")}
                    madeInIndia={t("footer.madeInIndia")}
                />
                <StickyCta
                    isVisible={isStickyCtaVisible}
                    onClick={handleBookLorryNow}
                    isCompactPostingOpen={isCompactPostingOpen}
                    ctaLabel={t("hero.cta")}
                />
            </main>
        </>
    );
};

export default LcvRouteLandingPage;
